import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';
import {
  Settings, Lock, Globe, User, CheckCircle, AlertCircle, Eye, EyeOff
} from 'lucide-react';

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user, switchLanguage } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: i18n.language === 'he' ? 'הסיסמאות לא תואמות' : 'Passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: i18n.language === 'he' ? 'סיסמה חדשה חייבת להיות לפחות 6 תווים' : 'New password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setMessage({ type: 'success', text: i18n.language === 'he' ? 'הסיסמה שונתה בהצלחה' : 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to change password';
      setMessage({ type: 'error', text: errMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-100 flex items-center gap-3">
          <Settings className="w-7 h-7 text-gray-400" />
          {t('nav.settings')}
        </h1>
      </div>

      {/* User Info */}
      <div className="card p-6 mb-6">
        <h2 className="font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-brand-400" />
          {i18n.language === 'he' ? 'פרטי משתמש' : 'User Info'}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="label">{t('auth.email')}</p>
            <p className="text-gray-200">{user?.email}</p>
          </div>
          <div>
            <p className="label">{t('auth.displayName')}</p>
            <p className="text-gray-200">{user?.displayName}</p>
          </div>
        </div>
      </div>

      {/* Language */}
      <div className="card p-6 mb-6">
        <h2 className="font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-cyan-400" />
          {t('common.language')}
        </h2>
        <div className="flex gap-3">
          <button
            onClick={() => switchLanguage('en')}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all
              ${i18n.language === 'en'
                ? 'border-brand-500 bg-brand-500/10 text-brand-300'
                : 'border-[#2a3348] text-gray-400 hover:border-gray-600'
              }`}
          >
            English
          </button>
          <button
            onClick={() => switchLanguage('he')}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all
              ${i18n.language === 'he'
                ? 'border-brand-500 bg-brand-500/10 text-brand-300'
                : 'border-[#2a3348] text-gray-400 hover:border-gray-600'
              }`}
          >
            עברית
          </button>
        </div>
      </div>

      {/* Change Password */}
      <div className="card p-6">
        <h2 className="font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-amber-400" />
          {i18n.language === 'he' ? 'שינוי סיסמה' : 'Change Password'}
        </h2>

        {message && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg mb-4 text-sm
            ${message.type === 'success'
              ? 'bg-green-500/10 border border-green-500/20 text-green-400'
              : 'bg-red-500/10 border border-red-500/20 text-red-400'
            }`}>
            {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message.text}
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="label">
              {i18n.language === 'he' ? 'סיסמה נוכחית' : 'Current Password'}
            </label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                className="input-field pr-12"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="label">
              {i18n.language === 'he' ? 'סיסמה חדשה' : 'New Password'}
            </label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                className="input-field pr-12"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="label">
              {i18n.language === 'he' ? 'אישור סיסמה חדשה' : 'Confirm New Password'}
            </label>
            <input
              type="password"
              className="input-field"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            {i18n.language === 'he' ? 'שנה סיסמה' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
